import { createApp } from 'vue'
import { createPinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import { createGtm } from '@gtm-support/vue-gtm'
import App from './App.vue'
import router from './router'
import './assets/main.css'
import { useAnalytics } from './composables/useAnalytics'
import { useCookieConsent } from './composables/useCookieConsent'
import { useChunkErrorHandler } from './composables/useChunkErrorHandler'

const app = createApp(App)
const pinia = createPinia()

// Get cookie consent state
const { hasConsent } = useCookieConsent()
const { handleChunkError } = useChunkErrorHandler()

// Initialize Sentry (always - error monitoring is legitimate interest)
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development',
    
    // Full feature integrations
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      })
    ],
    
    // Performance Monitoring
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Track API calls to these targets
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/.*\.supabase\.co\//,
      /^https:\/\/.*\.vercel\.app\//
    ],
    
    // Privacy - no PII by default
    sendDefaultPii: false,
    
    // Debug in development
    beforeSend(event, hint) {
      if (import.meta.env.DEV) {
        console.log('✅ Sentry capturing event:', event.event_id)
      }
      return event
    }
  })
  
  // Expose Sentry globally for testing (dev only)
  if (import.meta.env.DEV) {
    window.Sentry = Sentry
  }
  
  console.log('✅ Sentry initialized (full features) -', import.meta.env.VITE_SENTRY_ENVIRONMENT || 'development')
}

// Initialize analytics only with user consent
if (hasConsent('analytics')) {
  // Initialize Google Tag Manager
  if (import.meta.env.VITE_GTM_ID) {
    app.use(createGtm({
      id: import.meta.env.VITE_GTM_ID,
      enabled: true,
      vueRouter: router,
      debug: import.meta.env.DEV
    }))
    console.log('✅ GTM initialized (with consent)')
  }

  // Initialize Mixpanel
  const analytics = useAnalytics()
  analytics.init()
  console.log('✅ Mixpanel initialized (with consent)')

  // Initialize Microsoft Clarity
  if (import.meta.env.VITE_CLARITY_ID) {
    (function(c,l,a,r,i,t,y){
      c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
      t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
      y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", import.meta.env.VITE_CLARITY_ID);
    console.log('✅ Clarity initialized (with consent)')
  }
} else {
  console.log('ℹ️ Analytics not initialized (no consent)')
}

app.use(pinia)
app.use(router)

// Global error handler for uncaught chunk loading errors
app.config.errorHandler = (err, instance, info) => {
  // Try to handle as chunk error first
  const handled = handleChunkError(err, {
    tags: { errorType: 'vue-error' },
    extra: { info, component: instance?.$options?.name }
  });

  if (!handled) {
    // Not a chunk error or reload failed - log to Sentry
    Sentry.captureException(err, {
      tags: { errorType: 'vue-error' },
      extra: { info, component: instance?.$options?.name }
    });
    console.error('Vue error:', err, info);
  }
};

app.mount('#app')

